// This file follows the helpers-on-bottom pattern because there's a lot of them and I think it makes the
// file read more clearly.

import {objectFromEntries} from '@benchling/js-utils';
import gql from 'graphql-tag';
import {
  ArgumentNode,
  assertObjectType,
  getNamedType,
  GraphQLCompositeType,
  Kind,
  assertCompositeType,
  SelectionSetNode,
  isUnionType,
  GraphQLObjectType,
} from 'query-api-graphql';

import {ArchiveStatus, NOT_ARCHIVED_TEXT} from 'archive/ArchiveConstants';
import {assert} from 'Asserts';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {graphQLArgumentsToFilters, isQueryApiFilterKey} from 'query-api/QueryApiFilters';
import {QueryApiIntrospection, UseQueryApiIntrospectionOutput} from 'query-api/useQueryApiIntrospection';
import {
  DatetimeFilter,
  Filter,
  FilterKey,
  FILTERS,
  isDatetimeFilter,
  isMultiValueFilter,
  isSingleValueFilter,
  isStandardDatetimeFilter,
  Operator,
  StandardFilter,
  StandardRangeOperatorValue,
  VALUELESS_OPERATORS,
} from 'search/SearchConstants';
import APIUtil from 'util/APIUtil';
import removeNulls from 'util/removeNulls';
import {assertNever} from 'util/Types';

import {
  ResolveFilterValueApiIdsQuery,
  ResolveFilterValueApiIdsQueryVariables,
} from './__generated/ResolveFilterValueApiIdsQuery';
import {
  CompletedRelatedResultsFormState,
  FormState,
  getCompletedRelatedResultsFormStates,
} from './RegistrySchemaDataFrameCreationTypes';

const INDENTATION = 2;

export const RESOLVE_FILTER_VALUE_API_IDS_QUERY = gql`
  query ResolveFilterValueApiIdsQuery(
    $bioentityIds: [StrictString!]!
    $partyIds: [StrictString!]!
    $projectIds: [StrictString!]!
  ) @skipAudit @engComponent(name: ANALYSIS_TOOL) {
    users(ids: $partyIds) {
      ... on PartyInterface {
        id
        handle
      }
      ...InaccessibleInfoGraphQL
    }
    bioentities(ids: $bioentityIds) {
      ... on RegistryItemInterface {
        id
        displayLabel
      }
      ...InaccessibleInfoGraphQL
    }
    projects(ids: $projectIds) {
      ... on ProjectNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
    registries {
      id
      name
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Generate a human-readable version of the form state.
 *
 * This essentially applies a few transformations to the query string:
 * 1. Replace code-syntax tokens (brackets, parens, etc.) with human-friendly spacing, tokens, and terminology
 * INDENTATION. Replace API IDs in filter values with their names (for entity-based filters) or handles
 *    (for user-based filters)
 * 3. Replace field/type names with display names where possible
 *
 * @example
 * ```
 * // Form state corresponds to this query:
 * const query = `{
 *   enterprisetx__TransgenePlasmidItemsPreview(
 *     archiveReason: {anyOf: ["NOT_ARCHIVED"]}
 *     id: {anyOf: ["seq_2LEOljCJ", "seq_7PwpMxGu"]}
 *     createdAt: {lte: "2023-02-28T10:24:44-0500", gte: "2023-02-28T10:24:43-0500"}
 *     modifiedAt: {gte: "2023-02-28T10:24:46-0500"}
 *     creator: {anyOf: ["ent_sUnAyKfv"]}
 *     authors: {anyOf: ["ent_iCpKbvKq", "ent_kBeGJBCz"]}
 *   ) {
 *     id
 *     annotations {
 *       color
 *     }
 *     registrationOrigin {
 *       originEntry {
 *         id
 *       }
 *     }
 *     _enterprisetxOutputSampleAssay1(
 *       joinKey: "anotherTransgenePlasmidLink"
 *       first: 1
 *       sortArgs: {sortKey: MODIFIED_AT, sortOrder: DESCENDING}
 *     ) {
 *       id
 *       rtemperature
 *     }
 *   }
 * }`
 * await formStateToHumanReadableString(query)
 * // Output:
 * `
 * Transgene Plasmid
 *   Filters:
 *     Archived: Not archived
 *     Name: tp033, tp073
 *     Created: > 2023-02-28T10:24:43-0500 and < 2023-02-28T10:24:44-0500
 *     Modified: > 2023-02-28T10:24:46-0500
 *     Creator: L.Pasteur
 *     Author: person1, person2
 *
 *   Id
 *   Annotations
 *     color
 *   Registration Origin
 *     originEntry
 *       id
 *   Output Sample Assay Results, using Another Transgene Plasmid Link join field, only latest results
 *     Id
 *     R/Temperature
 * `
 * ```
 * @package
 */
export default async function formStateToHumanReadableString(
  formState: FormState,
  queryApiIntrospection: UseQueryApiIntrospectionOutput
): Promise<string> {
  assert(formState.entityListingFieldName != null, 'Cannot build a string description of an incomplete form');
  assert(
    queryApiIntrospection.status === QueryResultType.SUCCESS,
    'Cannot build a string description of a form without introspection'
  );

  const entityField = queryApiIntrospection.getQueryField(formState.entityListingFieldName);
  const entitySchemaType = assertObjectType(getNamedType(entityField?.type));
  const entitySchemaName = queryApiIntrospection.getTypeDisplayName(entitySchemaType);

  const filterString =
    formState.entityFilterArguments.length > 0
      ? `\n${await buildFilterString(formState.entityFilterArguments)}`
      : '';

  const entitySelectionsString = `\n${buildSelectionString(
    entitySchemaType,
    formState.entityFieldSelections,
    queryApiIntrospection,
    INDENTATION
  )}`;

  const relatedResultsString = `\n${getCompletedRelatedResultsFormStates(formState.relatedResultsFormStates)
    .map(
      (resultsFormState) =>
        `${buildRelatedResultsString(resultsFormState, entitySchemaType, queryApiIntrospection, INDENTATION)}`
    )
    .join('\n')}`;

  const result = `${entitySchemaName}${filterString}${entitySelectionsString}${relatedResultsString}`;

  // Strip any trailing newlines
  return result.replace(/\n$/g, '');
}

async function buildFilterString(
  entityFilterArguments: Array<ArgumentNode>,
  indent: number = INDENTATION
): Promise<string> {
  const padding = ' '.repeat(indent);

  const filters = graphQLArgumentsToFilters(entityFilterArguments);
  const apiIdToDisplayText = await getApiIdToDisplayTextMap(filters);

  let filterString = `${padding}Filters:\n`;
  for (const filter of filters) {
    assert(isQueryApiFilterKey(filter.key), 'Got non-query-api filter');

    const filterKeyName = FILTERS[filter.key].name;
    assert(filterKeyName != null, 'Got filter key without a defined name');

    filterString += `${padding}${padding}${filterKeyName}: `;
    if (isDatetimeFilter(filter)) {
      filterString += formatDatetimeFilterValue(filter);
    } else if (isStandardDatetimeFilter(filter)) {
      filterString += formatStandardDatetimeFilterValue(filter);
    } else if (filter.key === FilterKey.QUERY_API_ARCHIVE_REASON) {
      filterString += formatArchiveFilterValue(filter.value);
    } else if (
      filter.key === FilterKey.QUERY_API_AUTHOR ||
      filter.key === FilterKey.QUERY_API_CREATOR ||
      filter.key === FilterKey.QUERY_API_BIOENTITY_ID
    ) {
      filterString += formatApiIdentifiedMultiFilterValue(filter.value, apiIdToDisplayText);
    } else if (filter.key === FilterKey.QUERY_API_PROJECT || filter.key === FilterKey.QUERY_API_REGISTRY) {
      filterString += filter.value == null ? 'N/A' : apiIdToDisplayText[filter.value];
    } else {
      assert(false, `Got invalid filter key ${assertNever(filter.key)}`);
    }
    filterString += '\n';
  }

  return filterString;
}

interface ApiIdToDisplayText {
  [apiId: string]: string;
}

/**
 * Get map of API ID -> display text
 *
 * Inaccessible objects are ignored, which then fall back to showing their API IDs in the string.
 * This should rarely or never happen though, since the user just selected the corresponding item in the UI.
 */
async function getApiIdToDisplayTextMap(filters: Array<Filter>): Promise<ApiIdToDisplayText> {
  const allApiIds = [
    ...new Set(
      filters.flatMap((filter) =>
        isMultiValueFilter(filter)
          ? filter.value.filter((value) => APIUtil.apiIdToMaybeType(value) != null)
          : isSingleValueFilter(filter) &&
              filter.value != null &&
              APIUtil.apiIdToMaybeType(filter.value) != null
            ? [filter.value]
            : []
      )
    ),
  ];

  // Optimization to prevent making a GraphQL query for the names unless needed.
  if (allApiIds.length === 0) {
    return {};
  }

  const resolvedApiIds = await doQuerySafe<
    ResolveFilterValueApiIdsQuery,
    ResolveFilterValueApiIdsQueryVariables
  >(
    {query: RESOLVE_FILTER_VALUE_API_IDS_QUERY},
    {
      variables: {
        partyIds: allApiIds.filter((id) => APIUtil.isEntity(id)),
        bioentityIds: allApiIds.filter((id) => APIUtil.isBiologicalEntity(id)),
        projectIds: allApiIds.filter((id) => APIUtil.isSource(id)),
      },
    }
  );

  return objectFromEntries([
    ...removeNulls(
      resolvedApiIds.users.map<null | [string, string]>((party) =>
        isInaccessible(party) ? null : [party.id, party.handle]
      )
    ),
    ...removeNulls(
      resolvedApiIds.bioentities.map<null | [string, string]>((bioentity) =>
        isInaccessible(bioentity) ? null : [bioentity.id, bioentity.displayLabel]
      )
    ),
    ...removeNulls(
      resolvedApiIds.projects.map<null | [string, string]>((project) =>
        isInaccessible(project) ? null : [project.id, project.name]
      )
    ),
    ...resolvedApiIds.registries.map<[string, string]>((registry) => [registry.id, registry.name]),
  ]);
}

function formatDatetimeFilterValue({value}: DatetimeFilter): string {
  if (value.fromDatetime == null && value.toDatetime == null) {
    return 'None';
  } else if (value.fromDatetime != null && value.toDatetime == null) {
    return `> ${value.fromDatetime}`;
  } else if (value.fromDatetime == null && value.toDatetime != null) {
    return `< ${value.toDatetime}`;
  } else {
    return `> ${value.fromDatetime} and < ${value.toDatetime}`;
  }
}

function formatStandardDatetimeFilterValue({operator, value}: StandardFilter): string {
  if (VALUELESS_OPERATORS.includes(operator)) {
    return operator;
  } else if (operator === Operator.GTE) {
    return `>= ${value}`;
  } else if (operator === Operator.LTE) {
    return `<= ${value}`;
  } else {
    return `>= ${(value as StandardRangeOperatorValue).from} and <= ${
      (value as StandardRangeOperatorValue).to
    }`;
  }
}

function formatArchiveFilterValue(archiveReasons: Array<string>): string {
  return (
    archiveReasons
      // Without this replacement, the NOT_ARCHIVED status would be shown as "NOT_ARCHIVED" instead of what is
      // shown in the filter dropdown, which is "Not archived".
      .map((reason) => (reason === ArchiveStatus.NOT_ARCHIVED ? NOT_ARCHIVED_TEXT : reason))
      .join(', ')
  );
}

function formatApiIdentifiedMultiFilterValue(
  apiIds: Array<string>,
  apiIdToDisplayText: ApiIdToDisplayText
): string {
  return apiIds.map((apiId) => apiIdToDisplayText[apiId] ?? apiId).join(', ');
}

function buildSelectionString(
  type: GraphQLCompositeType,
  selectionSet: SelectionSetNode,
  queryApiIntrospection: QueryApiIntrospection,
  indent: number
): string {
  const padding = ' '.repeat(indent);

  let string = '';
  for (const selection of selectionSet.selections) {
    string += padding;
    if (selection.kind === Kind.FIELD) {
      assert(!isUnionType(type), 'Cannot have field selection on union type');
      const field = type.getFields()[selection.name.value];

      string += queryApiIntrospection.getFieldDisplayName(field);

      if (selection.selectionSet) {
        const subSelectionString = buildSelectionString(
          // assertCompositeType is safe since if a field has a selection set, its type must allow for
          // selection sets, and only composite types allow selection sets.
          assertCompositeType(getNamedType(field.type)),
          selection.selectionSet,
          queryApiIntrospection,
          indent + INDENTATION
        );
        string += `\n${subSelectionString}`;
      }
    } else if (selection.kind === Kind.INLINE_FRAGMENT) {
      const typeCondition = selection.typeCondition;
      assert(typeCondition != null, 'Cannot have inline fragment without type condition');
      string += typeCondition.name.value;

      const subSelectionString = buildSelectionString(
        assertCompositeType(queryApiIntrospection.graphQLSchema.getType(typeCondition.name.value)),
        selection.selectionSet,
        queryApiIntrospection,
        indent + INDENTATION
      );
      string += `\n${subSelectionString}`;
    } else if (selection.kind === Kind.FRAGMENT_SPREAD || assertNever(selection)) {
      assert(false, `Only FIELD and INLINE_FRAGMENT selections are supported; got ${selection.kind}`);
    }

    string += '\n';
  }
  // Strip final newline; caller should add it if it wants it. This makes the recursive calls easier to
  // handle.
  return string.replace(/\n$/g, '');
}

function buildRelatedResultsString(
  formState: CompletedRelatedResultsFormState,
  entityType: GraphQLObjectType,
  queryApiIntrospection: QueryApiIntrospection,
  indent: number
): string {
  const padding = ' '.repeat(indent);

  let string = padding;

  const backlinkField = entityType.getFields()[formState.backlinkFieldName];
  const resultSchemaType = assertObjectType(getNamedType(backlinkField.type));
  const joinField = resultSchemaType.getFields()[formState.joinKeyFieldName];

  // Add description of the result join
  string += `${queryApiIntrospection.getTypeDisplayName(
    resultSchemaType
  )} Results, using ${queryApiIntrospection.getFieldDisplayName(joinField)} join field, ${
    formState.onlyIncludeLatestItem ? 'only latest results' : 'all matching results'
  }\n`;

  // Add description of the result fields selected
  string += buildSelectionString(
    resultSchemaType,
    formState.backlinkSelections,
    queryApiIntrospection,
    indent + INDENTATION
  );

  return string;
}
